import {
    PlayerId,
    useGameServerConnection,
    usePixiRenderOnChange,
    usePlayers,
} from '@glark-newco/game-library';
import {Graphics as PixiGraphics} from '@pixi/graphics';
import {_ReactPixi, Container, Graphics, Sprite, Text} from '@pixi/react';
import {Assets} from 'pixi.js';
import {PropsWithChildren, useCallback, useMemo} from 'react';
import {Button} from './Button';
import {PopupData} from './RemoteCellLayout';
import {NOTEPAD, WRITING_FONT} from '../assetManifest';
import {playerCapacity} from '../components/AssignPlayersPanel';
import {textStyle} from '../constants';
import {useNetworkLinks, useNetworkOwnership} from '../state/networkHooks';
import {NodeId} from '../state/networkStore';
import {usePlayerObjectives} from '../state/PlayerObjectivesSlice';
import {usePlayerRings} from '../state/PlayerRingsSlice';
import {useRingDefinition} from '../state/RingDefinitionsSlice';
import {SaveObjectiveEvent, SupplyChainPayload, TransferRingEvent} from '../SupplyChainPayloadTypes';

interface TransferRingProps extends PropsWithChildren<_ReactPixi.ISprite> {
    x: number;
    y: number;
    playerId: PlayerId;
    setPopupActive: (data: PopupData | undefined) => void;
}

interface BookmarkTabProps extends PropsWithChildren<_ReactPixi.IGraphics> {
    direction: 'left' | 'right';
}

function BookmarkTab({width, height, direction, ...props}: BookmarkTabProps) {

    const w: number = width ?? 75;
    const h: number = height ?? 30;
    const m: number = direction == 'left' ? -1 : 1;

    const drawBookmarkTab = useCallback((graphics: PixiGraphics) => {
        graphics
            .clear()
            .lineStyle({width:1, color:0x000000})
            .beginFill('#f08')
            .drawPolygon([0, 0, m * (w - (h / 2)), 0, m * w, h / 2, m * (w - (h / 2)), h, 0, h])
            .endFill();
    }, [w, h, m]);

    return <Graphics {...props} draw={drawBookmarkTab}/>;
}

function BankObjectiveButton({playerId, setPopupActive}: {
    playerId: PlayerId,
    setPopupActive: (data: PopupData | undefined) => void
}) {
    const {localPlayer} = usePlayers();
    const {publishPlayerAction} = useGameServerConnection<SupplyChainPayload>();
    const localRings = usePlayerRings(localPlayer.playerId);
    const activeRing = useRingDefinition((localRings.length > 0) ? localRings[localRings.length - 1] : undefined);
    const playerObjectives = usePlayerObjectives(localPlayer.playerId);

    const activeObjective = useMemo(() =>
        playerObjectives.find(o => o.ringId === undefined)
    , [playerObjectives]);

    const ringMatchesObjective = useMemo(() =>
        (!!activeObjective) && activeRing?.colour === activeObjective?.ringColour
    , [activeObjective, localRings]);

    const bankObjective = useCallback(() => {
        if (localRings.length > 0) {
            publishPlayerAction({
                type: 'saveObjective',
                ringId: localRings[localRings.length - 1],
                playerId: localPlayer.playerId,
            } as SaveObjectiveEvent);
            setPopupActive(undefined);
        }
    }, [publishPlayerAction, localRings, playerId]);

    return <Button x={60} y={210} label={'Bank Ring'} onclick={bankObjective} enabled={ringMatchesObjective}/>;
}

function TransferRingButton({recipientPlayerId, setPopupActive}: {
    recipientPlayerId: PlayerId,
    setPopupActive: (data: PopupData | undefined) => void
}) {
    const {localPlayer} = usePlayers();
    const {publishPlayerAction} = useGameServerConnection<SupplyChainPayload>();
    const localRings = usePlayerRings(localPlayer.playerId);

    const links = useNetworkLinks();
    const ownership = useNetworkOwnership();

    const recipientRings = usePlayerRings(recipientPlayerId);
    const recipientObjectives = usePlayerObjectives(recipientPlayerId);

    const isRecipientLinked = useMemo(() => {
        const nodeIds: NodeId[] = Object.entries(ownership)
            .filter(([, pid]) => (pid === localPlayer.playerId) || (pid === recipientPlayerId))
            .map(([nodeIdStr]) => Number(nodeIdStr));
        return links.some(l => (nodeIds.includes(l.nodeIdA) && nodeIds.includes(l.nodeIdB)));
    }, [links, ownership, localPlayer.playerId, recipientPlayerId]);

    const recipientCapacity = useMemo(() => {
        const recipientTotal = recipientRings.length + recipientObjectives.filter(o => !!o.ringId).length;
        const objectivesComplete = recipientObjectives.every(o => !!o.ringId);
        return (recipientTotal < playerCapacity) && !objectivesComplete;
    }, [recipientRings, recipientObjectives]);

    const publishTransferRing = useCallback(() => {
        if (localRings.length > 0) {
            publishPlayerAction({
                type: 'transferRing',
                ringId: localRings[localRings.length - 1],
                sourcePlayerId: localPlayer.playerId,
                recipientPlayerId,
            } as TransferRingEvent);
            setPopupActive(undefined);
        }
    }, [publishPlayerAction, localRings, recipientPlayerId, localPlayer.playerId, setPopupActive]);

    if (!isRecipientLinked)
        return <></>;
    else
        return <Button x={60} y={210} label={'Pass Ring'}
            onclick={publishTransferRing}
            enabled={recipientCapacity && localRings.length > 0}
        />;
}

export function TransferRingPopup({x, y, playerId, setPopupActive, ...others}: TransferRingProps) {
    const {localPlayer, players} = usePlayers();
    const recipientRings = usePlayerRings(playerId);
    const recipientObjectives = usePlayerObjectives(playerId);

    const player = useMemo(() => players.find(p => p.playerId === playerId), [players, playerId]);
    const bankedObjectives = useMemo(() => {
        return recipientObjectives.filter(o => !!o.ringId);
    }, [recipientObjectives]);
    usePixiRenderOnChange([x, y, playerId]);

    const handWritingFont = Assets.get<FontFace>(WRITING_FONT).family;

    const summary = `✓ ${bankedObjectives.length} rings 'banked'\n☑ ${recipientRings.length} active rings`;

    const left = x > 1175;
    return (
        <Container {...others} x={left ? x - 325 : x} y={y} eventMode={'static'}
            onmouseenter={() => setPopupActive({x, y, playerId})}
            onmouseleave={() => setPopupActive(undefined)}
        >
            <Sprite texture={Assets.get(NOTEPAD)} x={left ? 0 : 35}>
                <Text text={player?.name ?? '--none--'} style={textStyle({
                    fontWeight: 'normal', fontFamily: handWritingFont, align: 'left',
                    fill: '#00f', wordWrap: true, wordWrapWidth: 200,
                })} x={50} y={70}/>
                <Text text={summary} style={textStyle({
                    fontWeight: 'normal', fontSize: '1.6em', fontFamily: handWritingFont, align: 'left',
                    fill: '#00f', wordWrap: true, wordWrapWidth: 200, lineHeight: 23,
                })} x={55} y={135}/>

                {playerId === localPlayer.playerId
                    ? <BankObjectiveButton playerId={playerId} setPopupActive={setPopupActive}/>
                    : <TransferRingButton recipientPlayerId={playerId} setPopupActive={setPopupActive}/>
                }
            </Sprite>
            <BookmarkTab x={left ? 325 : 0} y={75} direction={left ? 'left' : 'right'}/>
        </Container>
    );
}
